import { useMemo } from 'react';

import { useAppSelector } from '@/app/hooks';
import { useAllTopRevisionsQuery } from '@/app/services/donorSchedules';

export const useRevisionIncrement = (
  date?: string,
  fertilizationSiteId?: string,
) => {
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const { data: allTopRevisionNumbers } = useAllTopRevisionsQuery(
    {
      date,
    },
    {
      pollingInterval: 5000,
      skip: !date,
    },
  );

  const topRevisionNumber = useMemo(() => {
    let highestNumber = 0;

    const extractAllNumbers = (revisionStr: string): number[] => {
      const numbers: number[] = [];
      const matches = revisionStr.match(/\d+/g);
      if (matches) {
        for (const match of matches) {
          const num = parseInt(match, 10);
          if (!Number.isNaN(num)) {
            numbers.push(num);
          }
        }
      }
      return numbers;
    };

    if (allTopRevisionNumbers?.allTopRevisions) {
      const siteRevisions = fertilizationSiteId
        ? allTopRevisionNumbers.allTopRevisions.filter(
            (revision) => revision.fertilizationSiteId === fertilizationSiteId,
          )
        : allTopRevisionNumbers.allTopRevisions;

      for (const revision of siteRevisions) {
        if (!revision.revisionNumber) {
          continue;
        }

        const revisionStr = revision.revisionNumber.trim();
        const numbers = extractAllNumbers(revisionStr);

        for (const num of numbers) {
          if (num > highestNumber) {
            highestNumber = num;
          }
        }
      }
    }

    if (workingDonorSchedule?.revision) {
      const currentRevisionStr = workingDonorSchedule.revision.trim();
      const numbers = extractAllNumbers(currentRevisionStr);

      for (const num of numbers) {
        if (num > highestNumber) {
          highestNumber = num;
        }
      }
    }

    return highestNumber > 0 ? highestNumber : null;
  }, [
    allTopRevisionNumbers?.allTopRevisions,
    workingDonorSchedule?.revision,
    fertilizationSiteId,
  ]);

  const getNextRevisionNumber = useMemo(() => {
    if (topRevisionNumber === null) {
      return null;
    }
    return topRevisionNumber + 1;
  }, [topRevisionNumber]);

  return {
    topRevisionNumber,
    getNextRevisionNumber,
    allTopRevisionNumbers: allTopRevisionNumbers?.allTopRevisions,
  };
};
